import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEye, faEyeSlash, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { Site } from '@/types';

const FortnoxAuthentication: React.FC = () => {
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [showRefreshToken, setShowRefreshToken] = useState(false);

  // Fetch sites
  const { data: sites = [], isLoading: sitesLoading } = useQuery('sites', apiService.getSites);

  // Fetch Fortnox auth data for selected site
  const { data: fortnoxAuthData, isLoading: authLoading, refetch: refetchAuth } = useQuery(
    ['fortnoxAuth', selectedSiteId],
    () => apiService.getFortnoxOauth2s(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
    }
  );

  // Initialize with first site
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  const handleSiteChange = (siteId: number) => {
    setSelectedSiteId(siteId);
    setShowAccessToken(false);
    setShowRefreshToken(false);
  };

  const handleRefresh = () => {
    refetchAuth();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const isLoading = sitesLoading || authLoading;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">Fortnox Authentication Settings</h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="btn-icon btn-icon-secondary"
            title="Refresh"
          >
            <FontAwesomeIcon icon={faRefresh} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="alert alert-info flex items-center space-x-2">
          <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
          <span>Loading...</span>
        </div>
      )}


      {/* Authentication Settings */}
      {fortnoxAuthData?.[0] && (
        <div className="card">
          <div className="form-section">
            <div className="form-row">
              <div className="form-column">
                <div>
                  <label className="form-label">Site ID:</label>
                  <select
                    value={selectedSiteId || ''}
                    onChange={(e) => handleSiteChange(parseInt(e.target.value))}
                    className="form-input"
                    disabled={isLoading}
                  >
                    {sites.map((site: Site) => (
                      <option key={site.id} value={site.id}>
                        {site.url}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Access Tokens:</label>
                  <div className="relative">
                    <input
                      type={showAccessToken ? 'text' : 'password'}
                      value={fortnoxAuthData?.[0]?.access_token || ''}
                      className="form-input pr-10"
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={() => setShowAccessToken(!showAccessToken)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <FontAwesomeIcon icon={showAccessToken ? faEyeSlash : faEye} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-column">
                <div>
                  <label className="form-label">Refresh Token:</label>
                  <div className="relative">
                    <input
                      type={showRefreshToken ? 'text' : 'password'}
                      value={fortnoxAuthData?.[0]?.refresh_token || ''}
                      className="form-input pr-10"
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={() => setShowRefreshToken(!showRefreshToken)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <FontAwesomeIcon icon={showRefreshToken ? faEyeSlash : faEye} className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="form-label">Expires:</label>
                  <input
                    type="text"
                      value={fortnoxAuthData?.[0]?.token_expires || ''}
                    className="form-input"
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Information */}
      {fortnoxAuthData?.[0] && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Authentication Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-600">Created:</span>
              <span className="ml-2 text-gray-800">{formatDate(fortnoxAuthData?.[0]?.created_at)}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Last Updated:</span>
              <span className="ml-2 text-gray-800">{formatDate(fortnoxAuthData?.[0]?.updated_at)}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Token Status:</span>
              <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                fortnoxAuthData?.[0]?.token_expires && new Date(fortnoxAuthData[0].token_expires) > new Date()
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {fortnoxAuthData?.[0]?.token_expires && new Date(fortnoxAuthData[0].token_expires) > new Date()
                  ? 'Valid'
                  : 'Expired'
                }
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FortnoxAuthentication;
